import { Asset } from 'contentful'
import { FC, useState } from 'react'
import { ChevronRightIcon } from '~/components/icons'
import { TypeNavigationGroup, TypeNavigationLink } from '~/types/contentful'
import MobileNavLink from './MobileNavLink'
import classNames from 'classnames'
import { trackEvent } from '~/utils/tracking'

type MobileGroupProps = {
  data: MobileGroupField
  closeMenu: () => void
  handleMenuClick: (value: boolean) => void
}

type MobileGroupField = {
  title: string
  description?: string | undefined
  link: string
  icon?: Asset<'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined
  groups?: (TypeNavigationGroup<'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined)[] | undefined
  isAuthRequired?: boolean | undefined
  label?: string | undefined
  color?: string | undefined
}

type Menu = {
  active: string | null
  prev: string | null
}

const MobileGroup: FC<MobileGroupProps> = ({ data, closeMenu, handleMenuClick }) => {
  const [groupOpen, setGroupOpen] = useState<boolean>(false)
  const [menus, setMenus] = useState<Menu>({ active: null, prev: null })

  const handleClick = () => {
    trackEvent('toggle_website_menu', { click_menu_category: data?.title })

    if (!groupOpen) handleMenuClick(true)
    setGroupOpen(!groupOpen)
  }

  const handleToggleMenu = (value: string) => setMenus({ ...menus, active: value })

  return (
    <div className="flex flex-col gap-2" key={data?.title}>
      {!menus.active && (
        <div
          className="text-m text-neutral-90 cursor-pointer flex items-center justify-between py-5 border-b border-b-neutral-10"
          onClick={handleClick}
        >
          {data?.title}{' '}
          <ChevronRightIcon
            className={classNames(
              'cursor-pointer w-4 ml-2 hover:translate-x-[5px] transition duration-300 [&>path]:stroke-neutral-50 [&>path]:stroke-[4]',
              {
                'rotate-90': groupOpen,
              },
            )}
          />
        </div>
      )}
      {groupOpen &&
        data.groups?.map((item, index) => (
          <Container
            group={item}
            key={index}
            closeMenu={closeMenu}
            handleToggleMenu={handleToggleMenu}
          />
        ))}
    </div>
  )
}

type ContainerProps = {
  group: TypeNavigationGroup<'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined
  closeMenu: () => void
  handleToggleMenu: (value: string) => void
}

const Container: FC<ContainerProps> = ({ group, closeMenu, handleToggleMenu }) => {
  const [groupOpen, setGroupOpen] = useState<boolean>(false)

  if (!group) return <></>
  const { title, links } = group?.fields || {}

  const handleClick = () => {
    trackEvent('toggle_website_menu', { click_menu_category: title as string })
    handleToggleMenu(title as string)
    setGroupOpen(!groupOpen)
  }

  return (
    <div key={title}>
      <div className="block">
        <div className="ml-0">
          <div
            className="cursor-pointer border-b border-neutral-10 flex items-center justify-between py-5 px-0 max-md:text-mobile-s"
            onClick={handleClick}
          >
            {title}
            {links && (
              <ChevronRightIcon
                className={classNames(
                  'w-4 ml-2 transition duration-300 [&>path]:stroke-neutral-50 [&>path]:stroke-[4]',
                  {
                    'rotate-90': groupOpen,
                  },
                )}
              />
            )}
          </div>
        </div>
      </div>
      {groupOpen && (
        <div>
          {(links as TypeNavigationLink<'WITHOUT_UNRESOLVABLE_LINKS', string>[])?.map((link) => {
            if (!link) return <></>

            return (
              <MobileNavLink
                key={link.fields.link}
                title={link.fields.title}
                link={link.fields.link}
                isAuthRequired={link.fields.isAuthRequired}
                closeMenu={closeMenu}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

export default MobileGroup
